import { AuthData, useAuth } from '../contexts/auth';
import { Role } from '../types/domainModels';

export const ORG_ID_INSOMNIA = 315;
export const ORG_ID_GP = 85;
export const ORG_ID_REDBULL = 413;
export const DEVELOPER_EMAILS = [
  'travis.demint@useglass.com',
  'ron.thompson@useglass.com',
  'ron.thompson+nonadmin@useglass.com',
];

/**
 * Custom hook for determining if the user has the specified role.
 */
export function useHasRole(role: Role): boolean {
  const { data } = useAuth();
  return data?.role ? role === data?.role : false;
}

/**
 *
 * Custom hook for determining if the user is in the specified organization.
 */
export function useHasOrganization(orgId: number): boolean {
  const { data } = useAuth();
  return data?.organizationId ? orgId === data?.organizationId : false;
}

function isAuthorizedDeveloper(data: AuthData | null): boolean {
  if (!data?.email) {
    return false;
  }
  return DEVELOPER_EMAILS.includes(data.email.toLowerCase());
}

/**
 * Custom hook for checking if the user has access to a specific feature flag.
 * Admin users always have access to all feature flags.
 * For other users, checks feature flags in Auth0 user_metadata only.
 */
export enum FeatureFlagName {
  FF_GLA_2523 = 'FF_GLA_2523', // AI Coding
  FF_GLA_2675 = 'FF_GLA_2675', // Allocation Question
  FF_GLA_2833 = 'FF_GLA_2833', // Custom Variables
  TARGET_BY_GEO = 'TARGET_BY_GEO',
  DEVELOPER_PAGE = 'DEVELOPER_PAGE',
  QUOTE_TOOL_V2 = 'QUOTE_TOOL_V2',
  BILLING_PAGE = 'BILLING_PAGE',
}

export function useHasFeatureFlag(featureFlagName: FeatureFlagName): boolean {
  const { data } = useAuth();
  const isAdmin = useHasRole('admin');
  const isInsomniaUser = useHasOrganization(ORG_ID_INSOMNIA);
  const isRedbullUser = useHasOrganization(ORG_ID_REDBULL);
  const isDeveloper = isAuthorizedDeveloper(data);

  if (!data) {
    return false;
  }

  if (Object.keys(data.userMetadata).includes(featureFlagName)) {
    return true;
  }

  // Exhaustive switch for feature flags
  switch (featureFlagName) {
    case FeatureFlagName.FF_GLA_2523:
      if (isDeveloper || isAdmin) {
        return true;
      }
      return isInsomniaUser || isRedbullUser;

    case FeatureFlagName.FF_GLA_2675:
      return true;

    case FeatureFlagName.FF_GLA_2833:
      return true;

    case FeatureFlagName.TARGET_BY_GEO:
      if (isDeveloper) {
        return true;
      }
      return false;

    case FeatureFlagName.DEVELOPER_PAGE:
      if (isDeveloper) {
        return true;
      }
      return false;

    case FeatureFlagName.QUOTE_TOOL_V2:
      if (isDeveloper || isAdmin) {
        return true;
      }
      return false;

    case FeatureFlagName.BILLING_PAGE:
      if (isDeveloper || isAdmin) {
        return true;
      }
      return false;

    default: {
      // Exhaustiveness check
      const _exhaustiveCheck: never = featureFlagName;
      return _exhaustiveCheck;
    }
  }
}
